import { toast } from "@/hooks/use-toast";
import { DndContext, DragEndEvent, UniqueIdentifier } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  RowSelectionState,
  TableOptions,
  Table as TableType,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowDownUp,
  ArrowUpIcon,
  ChevronDown,
  Columns3,
  GripVertical,
} from "lucide-react";
import { Dispatch, HTMLProps, SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Command, CommandGroup, CommandItem, CommandList } from "./command";
import {
  DatatablePagination,
  DatatablePaginationProps,
} from "./datatable-pagination";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Sortable, SortableDragHandle, SortableItem } from "./sortable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  options?: Partial<TableOptions<TData>>;
  pagination?: DatatablePaginationProps;
  onRowClick?: (row: Row<TData>) => void;
  storageKey?: string;

  selection?: {
    state?: {
      selectionState: RowSelectionState;
      setSelectionState: Dispatch<SetStateAction<RowSelectionState>>;
    };
    getRowId: (row: TData) => string;
    enableSelectAll?: boolean;
    getAllRowIds?: () => Promise<{ id: string }[]>;
  };

  slim?: boolean;
  headless?: boolean;
  enableDragRows?: boolean;
  onRowDragged?: (data: {
    oldData: TData[];
    newData: TData[];
    indexFrom: number;
    indexTo: number;
  }) => void;
  onSortingChange?: (sorting: any) => void;
  allowInternalSorting?: boolean;
}

function SortableColumn({
  column,
  visibleColumns,
}: {
  column: Column<any, any>;
  visibleColumns: Column<any, any>[];
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: column.id,
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  const numberOfVisibleColumns = visibleColumns.filter(
    (c) => c.id != "select",
  ).length;

  const handleColumnVisibilityChange = () => {
    if (column.getIsVisible() || numberOfVisibleColumns < 8) {
      column.toggleVisibility(!column.getIsVisible());
    } else {
      toast({
        title: t("youCannotDisplayMoreThan8Columns"),
        variant: "destructive",
      });
    }
  };

  return (
    <CommandItem ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <div className="flex items-center justify-between space-x-2cursor-move w-full">
        <div className="flex items-center space-x-2 w-fit">
          <Checkbox
            onPointerDown={(e) => e.stopPropagation()}
            disabled={!column.getCanHide()}
            checked={column.getIsVisible()}
            onCheckedChange={handleColumnVisibilityChange}
          />
          <span className="text-sm font-['Raleway'] text-gray-700">
            {t(column.id)}
          </span>
        </div>
        <GripVertical className="ml-2 h-4 w-4 cursor-move" />
      </div>
    </CommandItem>
  );
}

const draggableColumn = {
  id: "drag",
  size: 1,
  cell: () => (
    <div className="flex justify-end">
      <SortableDragHandle
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
      >
        <DragHandleDots2Icon className="size-4" aria-hidden="true" />
      </SortableDragHandle>
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

function customIsSomeRowsSelected<T>(table: TableType<T>) {
  return Object.values(table.getState().rowSelection).some((v) => v === true);
}

export const DataTable = <TData, TValue>({
  pagination,
  data,
  columns: baseColumns,
  onRowClick,
  options,
  storageKey,
  slim,
  headless,
  enableDragRows,
  onRowDragged,
  selection,
  onSortingChange,
  allowInternalSorting,
  ...props
}: DataTableProps<TData, TValue> & Omit<HTMLProps<HTMLDivElement>, "data">) => {
  type SortableData = TData & { id: UniqueIdentifier };

  const { t } = useTranslation();

  const selectionColumn: ColumnDef<TData, TValue> = useMemo(
    () => ({
      id: "select",
      size: 52,
      enableHiding: false,
      enableSorting: false,
      header:
        selection?.enableSelectAll === true
          ? ({ table }) => (
              <div className="flex items-center gap-1">
                <Checkbox
                  checked={
                    table.getIsAllRowsSelected()
                      ? true
                      : customIsSomeRowsSelected(table)
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={async (checkState) => {
                    const someSelected = customIsSomeRowsSelected(table);
                    const allSelected = table.getIsAllRowsSelected();
                    const willSelect = Boolean(checkState);

                    if (someSelected || allSelected) {
                      table.resetRowSelection();
                      return;
                    }

                    if (!willSelect) {
                      table.resetRowSelection();
                      return;
                    }

                    table.toggleAllRowsSelected(true);
                  }}
                  aria-label="Select all rows"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      aria-label={t("moreSelectionOptions")}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit p-2" align="start">
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        className="justify-start"
                        onClick={(e) => {
                          e.stopPropagation();
                          table.resetRowSelection();
                          table.toggleAllRowsSelected(true);
                        }}
                      >
                        {t("selectOnlyThisPage")}
                      </Button>
                      {selection?.getAllRowIds && (
                        <Button
                          variant="ghost"
                          className="justify-start"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const getAll = selection?.getAllRowIds;
                            if (!getAll) return;
                            const allRowIds = await getAll();
                            const rowSelection = Object.fromEntries(
                              allRowIds.map((row) => [row.id, true] as const),
                            );
                            table.resetRowSelection();
                            table.setRowSelection(rowSelection);
                          }}
                        >
                          {t("selectAllResults")}
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )
          : undefined,

      cell: ({ row }) => (
        <div className="flex items-center">
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={(checked) => {
              row.toggleSelected?.(Boolean(checked));
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        </div>
      ),
    }),
    [selection, t],
  );

  const selectionEnabled = selection != undefined;

  const columns = [
    ...(selectionEnabled ? [selectionColumn] : []),
    ...(enableDragRows ? [draggableColumn] : []),
    ...baseColumns,
  ];

  function handleOnDraggableMove(event: {
    activeIndex: number;
    overIndex: number;
  }) {
    const newData = arrayMove(data, event.activeIndex, event.overIndex);
    onRowDragged?.({
      oldData: data,
      newData,
      indexFrom: event.activeIndex,
      indexTo: event.overIndex,
    });
  }

  const computedColumnOrder = useMemo(
    () => [
      ...(selectionEnabled ? [selectionColumn.id] : []),
      ...(draggableColumn ? [draggableColumn.id] : []),
      ...(options?.state?.columnOrder ?? []),
    ],
    [options?.state?.columnOrder, selectionEnabled, selectionColumn.id],
  );

  const table = useReactTable({
    ...options,
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: selection?.getRowId,
    onRowSelectionChange: selection?.state?.setSelectionState,
    state: {
      ...options?.state,
      columnOrder: computedColumnOrder,
      rowSelection: selection?.state?.selectionState,
    },
    enableRowSelection: selectionEnabled,
  });

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          order: options?.state?.columnOrder,
          visibility: options?.state?.columnVisibility,
        }),
      );
    }
  }, [
    options?.state?.columnOrder,
    options?.state?.columnVisibility,
    storageKey,
  ]);

  const columnOrder = table.getState().columnOrder;

  const commandListColumns = table
    .getAllColumns()
    .filter(
      (column) =>
        typeof column.accessorFn !== "undefined" && column.getCanHide(),
    )
    .sort((a, b) => {
      const aIndex = columnOrder.indexOf(a.id);
      const bIndex = columnOrder.indexOf(b.id);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = commandListColumns.findIndex(
        (column) => column.id === active.id,
      );
      const newIndex = commandListColumns.findIndex(
        (column) => column.id === over?.id,
      );

      const newOrder = arrayMove(commandListColumns, oldIndex, newIndex);
      const newOrderIds = newOrder.map((column) => column.id);

      table.setColumnOrder(newOrderIds);
    }
  };

  const tableSorting = table.getState().sorting;

  useEffect(() => {
    onSortingChange?.(tableSorting);
  }, [tableSorting]);

  return (
    <div {...props}>
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext items={commandListColumns.map((column) => column.id)}>
          {commandListColumns.length > 0 &&
            options?.state?.columnVisibility && (
              <div className="flex justify-end items-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline">
                      <Columns3 />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit p-0" align="end">
                    <Command className="space-y-2">
                      <p className="text-sm text-gray-500 font-['Raleway'] px-[8px] py-[4px]">
                        {t("dashboardSelectionColonnes")}
                      </p>
                      <CommandList>
                        <CommandGroup>
                          {commandListColumns.map((column) => (
                            <SortableColumn
                              key={column.id}
                              column={column}
                              visibleColumns={table.getVisibleFlatColumns()}
                            />
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
        </SortableContext>
        {/* Inline banners removed in favor of dropdown tied to the header checkbox */}
        {pagination && pagination.totalSize > 0 && (
          <div className="w-full my-4">
            <DatatablePagination
              pagination={pagination.pagination}
              setPage={pagination.setPage}
              totalSize={pagination.totalSize}
            />
          </div>
        )}
        <Table className="rounded-md border">
          {headless !== true && (
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-slate-100">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className="py-3 px-2"
                        style={{ width: `${header.getSize()}px` }}
                      >
                        {header.isPlaceholder ? null : allowInternalSorting ? (
                          <div
                            className={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center gap-1"
                                : ""
                            }
                            onClick={header.column.getToggleSortingHandler()}
                            title={
                              header.column.getCanSort()
                                ? header.column.getNextSortingOrder() === "asc"
                                  ? t('sortAscending')
                                  : header.column.getNextSortingOrder() ===
                                      "desc"
                                    ? t('sortDescending')
                                    : t('clearSort')
                                : undefined
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: <ArrowUpIcon className="w-4 h-4" />,
                              desc: <ArrowDownIcon className="w-4 h-4" />,
                            }[header.column.getIsSorted() as string] ??
                              (header.column.getCanSort() ? (
                                <ArrowDownUp className="w-4 h-4" />
                              ) : null)}
                          </div>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody>
            <Sortable
              value={data as SortableData[]}
              onMove={handleOnDraggableMove}
            >
              {table.getRowModel().rows?.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <SortableItem
                    value={(row.original as SortableData).id}
                    key={row.id}
                    asChild
                  >
                    <TableRow
                      key={row.id}
                      onClick={() => onRowClick?.(row)}
                      className={`${row.index % 2 == 1 ? "bg-slate-100 " : ""}
                    text-nowrap text-left px-6 ${onRowClick != null ? "cursor-pointer" : ""}`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}px` }}
                          className={slim !== true ? "py-5 " : "py-1"}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </SortableItem>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 px-3 py-4"
                  >
                    {t("empty")}
                  </TableCell>
                </TableRow>
              )}
            </Sortable>
          </TableBody>
        </Table>
      </DndContext>
    </div>
  );
};
